import { useI18n } from '../i18n';
import { Button } from '../components/Button';
import { useWaitlist } from '../components/WaitlistProvider';
import styles from './Pricing.module.css';

/**
 * One plan — free — and the shape of the house, said plainly.
 *
 * The free release (15 Sep 2026; the app repo's docs/BILLING.md tells the
 * story) ships Two Milks free and capped: a house holds three people and
 * shows the last 30 days of trips, and nothing older is ever deleted. The
 * cocoa card that used to carry the paid tier now carries those limits —
 * the brand surface stating the shape of the thing, not selling an upgrade.
 * No price appears anywhere because there is nothing for sale.
 *
 * The button opens the waitlist rather than starting anything, because the
 * app is not published yet.
 */
export function Pricing() {
  const { t } = useI18n();
  const { open } = useWaitlist();
  const p = t.pricing;

  return (
    <section className={styles.section} id="pricing">
      <div className={styles.intro}>
        <h2 className={styles.title}>{p.title}</h2>
        <p className={styles.body}>{p.body}</p>
      </div>

      <div className={styles.plans}>
        <article className={styles.free}>
          <h3 className={styles.name}>{p.free.name}</h3>
          <p className={styles.price}>{p.free.price}</p>
          <p className={styles.per}>{p.free.per}</p>
          <ul className={styles.items}>
            {p.free.items.map((item) => (
              <li key={item} className={styles.item}>
                <Tick />
                {item}
              </li>
            ))}
          </ul>
          <Button variant="outline" size="lg" block onClick={() => open('pricing-free')}>
            {p.free.cta}
          </Button>
        </article>

        {/* Cocoa fills the frame — the brand surface, carrying the house's
            shape rather than a price. */}
        <article className={styles.kept}>
          <h3 className={styles.name}>{p.kept.name}</h3>
          <p className={styles.note}>{p.kept.note}</p>
          <ul className={styles.items}>
            {p.kept.items.map((item) => (
              <li key={item} className={styles.item}>
                <Tick onCocoa />
                {item}
              </li>
            ))}
          </ul>
        </article>
      </div>

      <div className={styles.why}>
        <span className={styles.whyMark} aria-hidden="true" />
        <p className={styles.whyText}>
          <strong className={styles.whyTitle}>{p.whyTitle}</strong> {p.whyBody}
        </p>
      </div>
    </section>
  );
}

/* Bought green is the tick's colour by definition — it is the "this one is
   done" signal everywhere else in the product. On cocoa it would be 1.6:1, so
   the inverted card uses the cream instead. */
const Tick = ({ onCocoa }: { onCocoa?: boolean }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke={onCocoa ? 'var(--on-cocoa)' : 'var(--bought)'}
    strokeWidth="2.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 13l4 4 10-10" />
  </svg>
);
